<template>
  <div class="w-full">
    <div v-if="error.description" class="mb-32">
      <CardWrapper type-class="card--error" class="my-16">
        <template #body>
          <h3 v-if="error.title">
            {{ error.title }}
          </h3>
          {{ error.description }}
        </template>
      </CardWrapper>
    </div>
    <form @submit.prevent="sendAccountForm">
      <div class="grid col-1 md-col-2 w-full mb-0">
        <InputField
          id="first_name"
          v-model="accountForm.firstName"
          type="text"
          left-icon-class="icon-users"
          right-icon-class="icon-close input__right-icon--hover"
          :label="$t('first_name')"
          :has-error="
            v$.accountForm.firstName.$invalid &&
            v$.accountForm.firstName.$anyDirty
          "
          :has-right-reset-button="true"
          @on-right-click="accountForm.firstName = ''"
        />

        <InputField
          id="last_name"
          v-model="accountForm.lastName"
          type="text"
          left-icon-class="icon-users"
          right-icon-class="icon-close input__right-icon--hover"
          :label="$t('last_name')"
          :has-error="
            v$.accountForm.lastName.$invalid &&
            v$.accountForm.lastName.$anyDirty
          "
          :has-right-reset-button="true"
          @on-right-click="accountForm.lastName = ''"
        />
      </div>

      <InputField
        id="nickName"
        v-model="accountForm.nickName"
        type="text"
        left-icon-class="icon-mail-envelope"
        right-icon-class="icon-close input__right-icon--hover"
        :label="'Nickname'"
        :required="false"
        :has-error="
          v$.accountForm.nickName.$invalid && v$.accountForm.nickName.$anyDirty
        "
        :has-right-reset-button="true"
        @on-right-click="accountForm.nickName = ''"
      />

      <div class="mb-24">
        <SelectField
          v-if="languages.length"
          id="accountFormLang"
          v-model="accountForm.language"
          :options="languages"
          value-prop-name="locale"
          :placeholder="$t('select_your_language')"
          icon-class-left="icon-lang"
          icon-class-right="icon-simple-arrow-down"
          :has-error="
            v$.accountForm.language.$invalid &&
            v$.accountForm.language.$anyDirty
          "
          @change="onLanguageChange"
        />
      </div>
      <InputField
        id="email"
        v-model="accountForm.email"
        type="text"
        left-icon-class="icon-mail-envelope"
        right-icon-class="icon-close input__right-icon--hover"
        :label="$t('email')"
        :has-error="
          v$.accountForm.email.$invalid && v$.accountForm.email.$anyDirty
        "
        :has-right-reset-button="true"
        @on-right-click="accountForm.email = ''"
      />

      <InputField
        id="password"
        v-model="accountForm.password"
        type="password"
        left-icon-class="icon-padlock"
        :label="$t('password')"
        :has-error="
          v$.accountForm.password.$invalid && v$.accountForm.password.$anyDirty
        "
      />

      <ul class="simple-list mb-24 pl-16">
        <li
          class="simple-list__item"
          :class="{ 'simple-list__item--success': passwordLength }"
        >
          {{ $t('password_length_must_be') }}
        </li>
        <li
          class="simple-list__item"
          :class="{ 'simple-list__item--success': passwordHasNumbers }"
        >
          {{ $t('password_at_least_one_number') }}
        </li>
        <li
          class="simple-list__item"
          :class="{ 'simple-list__item--success': passwordHasCaps }"
        >
          {{ $t('password_at_least_one_capital_letter') }}
        </li>
      </ul>

      <InputField
        id="repeatPassword"
        v-model="repeatPassword"
        type="password"
        left-icon-class="icon-padlock"
        :label="$t('repeat_password')"
        :has-error="v$.repeatPassword.$invalid"
      />

      <CheckboxField
        v-model:checked="hasAcceptTermsOfUse"
        class="mb-24"
        field-id="termsOfUse"
        :label="$t('i_accept_the_terms_of_use')"
        link
      />

      <CheckboxField
        v-model:checked="hasAcceptDataPrivacyPolicy"
        class="mb-24"
        field-id="dataPrivacyPolicy"
        :label="$t('i_accept_the_data_privacy_policy')"
        link
      />

      <CheckboxField
        v-model:checked="accountForm.subscribed"
        class="mb-24"
        field-id="subscribed"
        :label="$t('i_want_to_register_to_the_newsletter')"
      />

      <button
        class="btn btn--purple"
        :class="{ 'btn--loading': loading }"
        :disabled="v$.$invalid || loading"
        type="submit"
      >
        <template v-if="loading">
          <LoadingDots />
        </template>
        <template v-else>
          {{ $t('sign_up') }}
          <span class="icon-simple-arrow-right btn__icon"></span>
        </template>
      </button>
    </form>
  </div>
</template>

<script setup>
import { requiredIf } from '@vuelidate/validators'
import { CardWrapper, LoadingDots } from '@snowshoe-frontend/shared-components'
import {
  InputField,
  SelectField,
  CheckboxField
} from '@snowshoe-frontend/form-fields-components'
import { reactive } from 'vue'
import { useLanguages } from '@/composables/useLanguages.js'
import useVuelidate from '@vuelidate/core'
import { storeToRefs } from 'pinia'
import { ref, computed } from 'vue'
import { useCommonStore } from '@/stores/commonStore'
import { useErrorsStore } from '@/stores/errorsStore'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { useNotificationsStore } from '@/stores/notificationsStore'
import { schemaToValidators } from '@snowshoe-frontend/utils-component'
import registerStageSchema from '@/openapi-schemas/register_employee.json'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  isOnboarding: {
    type: Boolean,
    default: false
  }
})

const accountForm = reactive({
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  language: '',
  nickName: '',
  subscribed: false
})
const repeatPassword = ref('')
const hasAcceptTermsOfUse = ref(false)
const hasAcceptDataPrivacyPolicy = ref(false)
const loading = ref(false)

const router = useRouter()
const { t } = useI18n()

const commonStore = useCommonStore()
const notificationsStore = useNotificationsStore()
const onboardingStore = useOnboardingStore()
const errorStore = useErrorsStore()

const { error } = storeToRefs(errorStore)
const { languages } = storeToRefs(commonStore)
const { changeLanguage } = useLanguages()

const passwordLength = computed(() => {
  return accountForm.password.length >= 8 && accountForm.password.length <= 20
})

const passwordHasNumbers = computed(() => {
  return new RegExp('[0-9]').test(accountForm.password)
})

const passwordHasCaps = computed(() => {
  return new RegExp('^(?=.*[A-Z])').test(accountForm.password)
})

const rules = {
  accountForm: {
    ...schemaToValidators(registerStageSchema)
  },
  repeatPassword: {
    required: requiredIf(function () {
      return accountForm.password.length > 0
    }),
    function(value) {
      return accountForm.password === value
    }
  },

  hasAcceptTermsOfUse: {
    accepted: (val) => val === true
  },
  hasAcceptDataPrivacyPolicy: {
    accepted: (val) => val === true
  }
}

const v$ = useVuelidate(
  rules,
  {
    accountForm,
    repeatPassword,
    hasAcceptTermsOfUse,
    hasAcceptDataPrivacyPolicy
  },
  { $autoDirty: true }
)

async function sendAccountForm() {
  if (v$.$invalid) {
    v$.$touch()
    return
  }
  try {
    loading.value = true
    const data = {
      ...accountForm,
      language: accountForm.language
    }
    await onboardingStore.saveAccountFormData(data, props.isOnboarding)
    props.isOnboarding
      ? router.push({ name: 'OnboardingFinishView' })
      : router.push({ name: 'RegisterSuccess' })
  } catch (e) {
    notificationsStore.setNotification({
      header: t('account_common_error'),
      body: 'Whoops! It seems we have problems with registration.',
      type: 'danger'
    })
  } finally {
    loading.value = false
  }
}
async function onLanguageChange() {
  const language = languages.value.find(
    (lang) => lang.locale === accountForm.language
  )

  if (!language.locale) return

  await changeLanguage(language.locale)
}
</script>
